import { useState } from "react";
import { useAppData } from "@/data/AppDataContext";
import { toast } from "sonner";
import type { Job, ContractorOpportunity } from "@/data/types";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  CheckCircle,
  XCircle,
  MapPin,
  Calendar,
  DollarSign,
  Package,
  User,
  AlertTriangle,
} from "lucide-react";
import { StatusBadge } from "@/components/StatusBadge";
import { formatAddress } from "@/lib/addressUtils";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  job: Job;
  opportunity: ContractorOpportunity;
}

export function ContractorAcceptRejectDialog({
  open,
  onOpenChange,
  job,
  opportunity,
}: Props) {
  const { setJobs, setOpportunities } = useAppData();
  const [mode, setMode] = useState<"view" | "rejecting">("view");
  const [rejectionReason, setRejectionReason] = useState("");
  const [done, setDone] = useState<"accepted" | "rejected" | null>(null);

  const today = new Date().toISOString().split("T")[0];

  const handleAccept = () => {
    setOpportunities((prev) =>
      prev.map((o) =>
        o.id === opportunity.id
          ? {
              ...o,
              responseStatus: "Accepted" as const,
              acceptedDate: today,
              responseDate: today,
            }
          : o
      )
    );
    setJobs((prev) =>
      prev.map((j) =>
        j.id === job.id
          ? {
              ...j,
              dispatchStatus: "Assigned" as const,
              contractorAssignmentStatus: "Accepted",
              currentStage:
                j.currentStage === "Available for Contractor Assignment"
                  ? ("Assigned and Dispatched" as const)
                  : j.currentStage,
              activityHistory: [
                ...j.activityHistory,
                {
                  id: `act-${Date.now()}`,
                  date: today,
                  action: `Contractor accepted job assignment`,
                  user: j.assignedSubcontractor || "Contractor",
                  noteType: "subcontractor" as const,
                },
              ],
            }
          : j
      )
    );
    setDone("accepted");
    setTimeout(() => {
      setDone(null);
      setMode("view");
      onOpenChange(false);
    }, 1500);
  };

  const handleReject = () => {
    if (!rejectionReason.trim()) return;
    setOpportunities((prev) =>
      prev.map((o) =>
        o.id === opportunity.id
          ? {
              ...o,
              responseStatus: "Rejected" as const,
              rejectionReason: rejectionReason.trim(),
              rejectedDate: today,
              responseDate: today,
              isWinner: false,
            }
          : o
      )
    );
    setJobs((prev) =>
      prev.map((j) =>
        j.id === job.id
          ? {
              ...j,
              dispatchStatus: "Sent to Contractor Network" as const,
              contractorAssignmentStatus: "Rejected — Reassignment Needed",
              assignedSubcontractor: "",
              assignmentType: "Unassigned" as const,
              activityHistory: [
                ...j.activityHistory,
                {
                  id: `act-${Date.now()}`,
                  date: today,
                  action: `Contractor rejected assignment. Reason: ${rejectionReason.trim()}`,
                  user: j.assignedSubcontractor || "Contractor",
                  noteType: "subcontractor" as const,
                },
              ],
            }
          : j
      )
    );
    setDone("rejected");
    toast.warning(`${job.id} needs reassignment`, {
      description: `Contractor declined: "${rejectionReason.trim()}". Job is now unassigned and ready for reassignment.`,
      duration: 8000,
      action: {
        label: "View Job",
        onClick: () => window.location.href = `/jobs/${job.id}`,
      },
    });
    setTimeout(() => {
      setDone(null);
      setMode("view");
      setRejectionReason("");
      onOpenChange(false);
    }, 1500);
  };

  if (done) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md">
          <div className="flex flex-col items-center gap-3 py-10">
            {done === "accepted" ? (
              <>
                <CheckCircle className="h-12 w-12 text-success" />
                <p className="text-base font-bold text-success">
                  Job Accepted!
                </p>
                <p className="text-xs text-muted-foreground">
                  You're confirmed for this installation.
                </p>
              </>
            ) : (
              <>
                <XCircle className="h-12 w-12 text-destructive" />
                <p className="text-base font-bold text-destructive">
                  Job Declined
                </p>
                <p className="text-xs text-muted-foreground">
                  The operations team has been notified.
                </p>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) setMode("view");
        onOpenChange(v);
      }}
    >
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-base">
            {mode === "rejecting" ? "Decline Job" : "Job Assignment"}
          </DialogTitle>
          <DialogDescription className="text-xs">
            {job.id} · {job.projectType}
          </DialogDescription>
        </DialogHeader>

        {mode === "rejecting" ? (
          <div className="space-y-4">
            <div className="rounded-lg border border-warning/30 bg-warning/5 p-3">
              <div className="flex items-center gap-2 mb-1">
                <AlertTriangle className="h-4 w-4 text-warning" />
                <span className="text-xs font-bold text-warning">
                  Declining this job
                </span>
              </div>
              <p className="text-[11px] text-muted-foreground">
                This will notify the operations team and the job will be
                reassigned to another contractor.
              </p>
            </div>

            <div>
              <label className="text-xs font-medium mb-1.5 block">
                Reason for declining{" "}
                <span className="text-destructive">*</span>
              </label>
              <Textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="e.g., Schedule conflict, too far from service area, not enough crew available..."
                className="text-sm"
                rows={3}
              />
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setMode("view")}
              >
                Back
              </Button>
              <Button
                variant="destructive"
                size="sm"
                disabled={!rejectionReason.trim()}
                onClick={handleReject}
              >
                <XCircle className="h-3.5 w-3.5" /> Confirm Decline
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Job summary */}
            <div className="rounded-lg border bg-muted/20 p-3 space-y-2">
              <div className="flex items-center gap-2 text-xs">
                <User className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="font-medium">{job.customerName}</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <MapPin className="h-3.5 w-3.5" />
                {formatAddress(job.address)}
              </div>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Calendar className="h-3.5 w-3.5" />
                {job.scheduledInstallDate || job.targetInstallDate || "TBD"}
                {job.timeSlot && ` · ${job.timeSlot}`}
              </div>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Package className="h-3.5 w-3.5" />
                {job.projectType} · {job.scopeSummary}
              </div>
              {opportunity.bidAmount && (
                <div className="flex items-center gap-2 text-xs">
                  <DollarSign className="h-3.5 w-3.5 text-success" />
                  <span className="font-bold text-success">
                    ${opportunity.bidAmount.toLocaleString()}
                  </span>
                  <span className="text-muted-foreground">your bid</span>
                </div>
              )}
            </div>

            {/* Status */}
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">Status:</span>
              <StatusBadge
                type="dispatch"
                value="Awaiting Response"
              />
            </div>

            {/* Actions */}
            <div className="grid grid-cols-2 gap-3 pt-2 border-t">
              <Button
                variant="outline"
                className="h-12 border-destructive/30 text-destructive hover:bg-destructive/5"
                onClick={() => setMode("rejecting")}
              >
                <XCircle className="h-4 w-4 mr-1.5" /> Decline
              </Button>
              <Button className="h-12" onClick={handleAccept}>
                <CheckCircle className="h-4 w-4 mr-1.5" /> Accept Job
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
